import type { BillingAccount, BillingInfo } from '@ctct/domain-layer';
import type { BillingInfoFormPageStore } from '@ctct/state-client-layer';

import type { UIService } from '../../_shared/Form/services/UIService';
import type { BillingInfoFormValues } from '../types';

export class DataPreparationService {
  constructor(
    private pageStore: BillingInfoFormPageStore,
    private billingInfo: BillingInfo,
    private billingAccount: BillingAccount,
    private getDefaultValues: () => BillingInfoFormValues,
    private uiService: UIService<BillingInfoFormValues>
  ) {}

  public api() {
    return {
      getValuesForApiRequest: () => {
        // If `isDisplayingCard` is `true`, assume the form is not mounted.
        // In that case, retrieve the default values from the backend.
        if (this.pageStore.getState().billingInfoFormIsDisplayingCard) {
          return this.createApiPayload(this.getDefaultValues());
        }

        if (!this.uiService.getFormValuesFn) throw new Error('get form values function is not added');

        // If the form is mounted, retrieve the current form values.
        const formValues = this.uiService.getFormValuesFn();

        return this.createApiPayload(formValues);
      },

      // on legacy only
      getRawFormValues: () => {
        if (this.pageStore.getState().billingInfoFormIsDisplayingCard) {
          return null;
        }

        if (!this.uiService.getFormValuesFn) throw new Error('get form values function is not added');

        return this.uiService.getFormValuesFn();
      },

      getCustomer: (currency: string) => {
        // it's a copy paste of getValuesForCheckoutApiRequest, but without form validation
        const uiIsNotMounted = !this.uiService.getFormValuesFn;

        if (this.pageStore.getState().billingInfoFormIsDisplayingCard || uiIsNotMounted) {
          // we don't save `currentTaxAgreement` on the BE;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
          const { currentTaxAgreement, ...otherDefaultValues } = this.getDefaultValues();

          return {
            ...this.legacyCreateRestOfCustomerForPayment(this.billingInfo.getContactEmailAddress(), currency),
            ...otherDefaultValues,
            // if is diplaying card -- it is a customer
            // if it is a customer -- all required values should be defined
            state: otherDefaultValues.state ?? '',
            address2: otherDefaultValues.address2 ?? '',
            vatNumber: otherDefaultValues.vatNumber ?? '',
          };
        }

        const newFormValues = this.api().getValuesForApiRequest();

        return {
          ...this.legacyCreateRestOfCustomerForPayment(this.billingInfo.getContactEmailAddress(), currency),
          firstname: newFormValues.first_name,
          lastname: newFormValues.last_name,
          companyname: newFormValues.company_name,
          address1: newFormValues.address1,
          address2: newFormValues.address2,
          city: newFormValues.city,
          zipcode: newFormValues.postal_code4
            ? `${newFormValues.postal_code}-${newFormValues.postal_code4}`
            : newFormValues.postal_code,
          phone: newFormValues.phone,
          state: newFormValues.state,
          country: newFormValues.country,
          email: newFormValues.email,
          vatNumber: newFormValues.non_us_tax_exemption_number || '',
        };
      },

      getValuesForCheckoutApiRequest: async (currency: string) => {
        if (this.pageStore.getState().billingInfoFormIsDisplayingCard) {
          // we don't save `currentTaxAgreement` on the BE;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
          const { currentTaxAgreement, ...otherDefaultValues } = this.getDefaultValues();

          return {
            ...this.legacyCreateRestOfCustomerForPayment(this.billingInfo.getContactEmailAddress(), currency),
            ...otherDefaultValues,
            // if is diplaying card -- it is a customer
            // if it is a customer -- all required values should be defined
            state: otherDefaultValues.state ?? '',
            address2: otherDefaultValues.address2 ?? '',
            vatNumber: otherDefaultValues.vatNumber ?? '',
          };
        }

        const isFormValid = await this.uiService.triggerValidation?.();

        if (!isFormValid) {
          throw new Error();
        }

        const newFormValues = this.api().getValuesForApiRequest();

        return {
          ...this.legacyCreateRestOfCustomerForPayment(this.billingInfo.getContactEmailAddress(), currency),
          firstname: newFormValues.first_name,
          lastname: newFormValues.last_name,
          companyname: newFormValues.company_name,
          address1: newFormValues.address1,
          address2: newFormValues.address2,
          city: newFormValues.city,
          zipcode: newFormValues.postal_code4
            ? `${newFormValues.postal_code}-${newFormValues.postal_code4}`
            : newFormValues.postal_code,
          phone: newFormValues.phone,
          state: newFormValues.state,
          country: newFormValues.country,
          email: newFormValues.email,
          vatNumber: newFormValues.non_us_tax_exemption_number || '',
        };
      },
    };
  }

  public data() {
    return {};
  }

  public handlers() {
    return {};
  }

  private createApiPayload(formValues: BillingInfoFormValues) {
    let postal_code = formValues.zipcode;
    let postal_code4 = '';

    if (formValues.country === 'US' && postal_code.length > 5) {
      postal_code4 = postal_code.replace('-', '').slice(5);
      postal_code = postal_code.slice(0, 5);
    }

    return {
      first_name: formValues.firstname,
      last_name: formValues.lastname,
      address1: formValues.address1,
      address2: formValues.address2 ?? '',
      company_name: formValues.companyname,
      city: formValues.city,
      state: formValues.state ?? '',
      country: formValues.country,
      postal_code,
      postal_code4,
      email: formValues.email,
      phone: formValues.phone,
      tax_exemption_agreement: formValues.currentTaxAgreement,
      non_us_tax_exemption_number: formValues.vatNumber || undefined,
    };
  }

  // this is a function from legacy codebase,
  // it is needed to be reviewed with BIA if all the data makes sense
  private legacyCreateRestOfCustomerForPayment(contactEmail: string, currencyCd: string) {
    return {
      customerId: this.billingAccount.getCustomerId(),
      userUuid: null,
      contactEmail,
      billingType: {
        name: 'R', // ??
      },
      customerInSingleBilling: false,
      billingLetterOption: this.billingInfo.getBillingLetterOption() || 'Y',
      currencyCd,
      billingPeriod: null,
      billingDayOfMonth: 0,
      addressValidationRequired: true,
      noPrepayAutoRenewOptin: 0,
      addressProvided: true,
      singleBilledAccount: false,
      partnerBilledAccount: false,
      daysLeftInBillingPeriod: this.billingInfo.getDaysLeftInPeriod(),
      accountStatus: this.billingInfo.getAccountStatus(),
      nextChargeScheduled: this.billingInfo.getNextChargeScheduled(),
    };
  }
}
